import {
  Card,
  Heading,
  Paragraph,
  Tag,
  Textfield,
} from "@digdir/designsystemet-react";
import { CheckmarkIcon } from "@navikt/aksel-icons";
import { useMutation } from "@tanstack/react-query";
import { type ChangeEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { httpClient } from "@/api/client";
import type {
  OppgjørForPeriode,
  ValidationProblemDetails,
} from "@/api/generated";
import { postInnrapporteringValidateMutation } from "@/api/generated/@tanstack/react-query.gen";
import { Page } from "@/components";
import { UploadFileButton } from "@/components/UploadFileButton";
import { ValidationProblems } from "@/components/ValidationProblems";
import { DokumentasjonsLenker } from "@/features/dokumentasjon/DokumentasjonsLenker";
import { useToaster } from "@/features/toaster";
import { cn } from "@/utils";
import { MaaledataPeriodeOppsummering } from "../periode/MaaledataPeriodeOppsummering";

export const MaaledataValidatePage = () => {
  return (
    <Page withBackLink backLinkTo={"history"}>
      <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
        <div className="max-w-card space-y-6">
          <HeaderSection />
          <ValideringsFormSection />
        </div>
        <VeiledningSection className="max-w-small-card mt-4 lg:m-0" />
      </div>
    </Page>
  );
};

const VeiledningSection = ({ className }: { className?: string }) => {
  return (
    <div className={cn("", className)}>
      <DokumentasjonsLenker className="lg:self-end" />
    </div>
  );
};

const HeaderSection = ({ className }: { className?: string }) => {
  const { t } = useTranslation();

  return (
    <div className={cn("", className)}>
      <Tag className="mb-2 bg-balder-support-surface-tinted! text-balder-text-support-default!">
        {t("pages.validering.tag")}
      </Tag>
      <Heading data-size={"lg"} className="mb-4">
        {t("pages.validering.tittel")}
      </Heading>
      <Paragraph>{t("pages.validering.description")}</Paragraph>
    </div>
  );
};

const ValideringsFormSection = ({ className }: { className?: string }) => {
  const { t } = useTranslation();

  const validationMutation = useMutation({
    ...postInnrapporteringValidateMutation({
      client: httpClient,
    }),
  });
  const toaster = useToaster();

  const [snittPrisInput, setSnittPrisInput] = useState<string>("0.5");
  const [uploadedFileIsValid, setUploadedFileIsValid] = useState<
    boolean | null
  >(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const [fileUploadError, setFileUploadError] =
    useState<ValidationProblemDetails | null>(null);
  const [validationResponse, setValidationResponse] =
    useState<OppgjørForPeriode | null>(null);

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      throw new Error("No file selected");
    }

    const parsedSnittPris = Number.parseFloat(snittPrisInput);
    if (
      snittPrisInput === "" ||
      snittPrisInput === "." ||
      !Number.isFinite(parsedSnittPris)
    ) {
      setFileUploadError({
        message: t("pages.validering.valideringsfeil-melding"),
      } as ValidationProblemDetails);
      setUploadedFileIsValid(false);
      return;
    }

    setFileUploadError(null);
    setFileUploadLoading(true);
    setUploadedFileName(file.name);
    setUploadedFileIsValid(null);
    setValidationResponse(null);

    validationMutation.mutate(
      {
        body: { file: file },
        query: { snittpris: parsedSnittPris },
        headers: { "Content-Type": "multipart/form-data" },
      },
      {
        onError: (error) => {
          const problemDetails =
            error.response?.data ??
            ({
              message: t("pages.validering.opplasting-feil", {
                context: "opplasting",
              }),
            } as ValidationProblemDetails);
          setFileUploadError(problemDetails);
          setUploadedFileIsValid(false);
        },
        onSuccess: async (res) => {
          toaster.success({
            messageKey: "pages.validering.lastet-opp-suksess",
          });
          setUploadedFileIsValid(true);
          setValidationResponse(res);
        },
        onSettled: () => {
          setFileUploadLoading(false);
        },
      },
    );
  };

  const snittPrisFieldId = useId();

  const handleSnittPrisChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const digitsAndDotOnly = rawValue.replace(/[^\d.]/g, "");
    const firstDotIndex = digitsAndDotOnly.indexOf(".");
    const normalizedValue =
      firstDotIndex === -1
        ? digitsAndDotOnly
        : `${digitsAndDotOnly.slice(0, firstDotIndex + 1)}${digitsAndDotOnly
            .slice(firstDotIndex + 1)
            .replace(/\./g, "")}`;

    setSnittPrisInput(normalizedValue);
  };

  return (
    <div className={cn("space-y-6", className)}>
      <div>
        <Textfield
          id={snittPrisFieldId}
          type={"text"}
          inputMode="decimal"
          pattern="[0-9]*\\.?[0-9]*"
          label={<span>{t("pages.validering.spottpris-label")}</span>}
          suffix="NOK"
          onChange={handleSnittPrisChange}
          value={snittPrisInput}
        />
      </div>
      <Card
        className={`border-dashed ${fileUploadLoading ? "animate-pulse motion-reduce:animate-none" : ""}`}
      >
        {fileUploadLoading === true ? (
          <span>{t("pages.validering.laster-opp-fil")}</span>
        ) : (
          <div className="flex flex-row justify-between items-center gap-4">
            {uploadedFileName != null ? (
              !uploadedFileIsValid ? (
                <span>
                  {t("pages.validering.opplasting-feil", {
                    context: uploadedFileName,
                  })}
                </span>
              ) : (
                <div className="flex gap-2 items-center">
                  <CheckmarkIcon fontSize={"1.5rem"} />
                  <span>
                    {t("pages.validering.opplasting-ok", {
                      context: uploadedFileName,
                    })}
                  </span>
                </div>
              )
            ) : (
              <span>{t("pages.validering.last-opp-fil")}</span>
            )}
            <UploadFileButton
              onFileChanged={handleUpload}
              acceptFileType=".csv"
              text={t("pages.validering.last-opp-knapp")}
              className="bg-balder-support-surface-tinted! text-balder-text-support-default!"
            />
          </div>
        )}
      </Card>
      {fileUploadLoading !== true && fileUploadError != null && (
        <ValidationProblems
          validationProblemDetails={fileUploadError}
          maxErrorsToShow={5}
          message={t("pages.validering.valideringsfeil-melding")}
        />
      )}
      {fileUploadLoading !== true &&
        fileUploadError == null &&
        validationResponse != null && (
          <MaaledataPeriodeOppsummering
            data={validationResponse}
            isTest={true}
          />
        )}
    </div>
  );
};
